import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import styles from './ResetPasswordPage.module.css'
import AuthService from '../../features/auth/api/AppService'
import { routes } from '../../shared/config/routes'
import { FieldWrapper } from '../../shared/ui'
import { Button } from '../../shared/ui/controls/Button/Button'
import { FormInput } from '../../shared/ui/controls/FormInput'
import { Loader } from '../../shared/ui'
import { getErrorMessage } from '../../shared/helpers/getErrorMessage'

const schema = z
  .object({
    newPassword: z.string().min(6, 'Mindestens 6 Zeichen'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwörter stimmen nicht überein',
    path: ['confirmPassword'],
  })

type FormValues = z.infer<typeof schema>

type TokenStatus = 'checking' | 'valid' | 'invalid' | 'already_used'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [done, setDone] = useState(false)
  const token = searchParams.get('token')
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>(token ? 'checking' : 'invalid')

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (!token) return
    AuthService.validateResetToken(token)
      .then(() => setTokenStatus('valid'))
      .catch((err) => {
        const code = (err as any)?.response?.data?.error
        setTokenStatus(code === 'RESET_TOKEN_ALREADY_USED' ? 'already_used' : 'invalid')
      })
  }, [token])

  if (tokenStatus === 'checking') {
    return (
      <div className={styles.page}>
        <div className={styles.main}>
          <Loader />
        </div>
      </div>
    )
  }

  if (tokenStatus === 'invalid') {
    return (
      <div className={styles.page}>
        <div className={styles.main}>
          <p className={styles.successText}>
            Dieser Link ist ungültig oder abgelaufen.
          </p>
          <div className={styles.footer}>
            <Link to={routes.auth.forgotPassword}>Neuen Link anfordern</Link>
          </div>
        </div>
      </div>
    )
  }

  if (tokenStatus === 'already_used') {
    return (
      <div className={styles.page}>
        <div className={styles.main}>
          <p className={styles.successText}>
            Dieser Reset-Link wurde bereits verwendet.<br />
            Bitte fordern Sie einen neuen Link an.
          </p>
          <div className={styles.footer}>
            <Link to={routes.auth.forgotPassword}>Neuen Link anfordern</Link>
          </div>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className={styles.page}>
        <div className={styles.main}>
          <p className={styles.successText}>
            Passwort erfolgreich geändert.<br />
            Bitte melden Sie sich erneut an.
          </p>
          <div className={styles.footer}>
            <Link to={routes.login}>Zur Anmeldung</Link>
          </div>
        </div>
      </div>
    )
  }

  const onSubmit = handleSubmit(async ({ newPassword }) => {
    clearErrors('root')
    try {
      await AuthService.resetPassword(token!, newPassword)
      setDone(true)
      setTimeout(() => navigate(routes.login), 3000)
    } catch (err) {
      const code = (err as any)?.response?.data?.error
      if (code === 'RESET_TOKEN_ALREADY_USED') {
        setTokenStatus('already_used')
      } else {
        setError('root', { type: 'server', message: getErrorMessage(err) })
      }
    }
  })

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <form onSubmit={onSubmit} noValidate className='!w-[300px] flex flex-col gap-[20px]'>
          <FieldWrapper label='Neues Passwort'>
            <FormInput
              name="newPassword"
              control={control}
              type="password"
              placeholder="Neues Passwort eingeben"
              autoComplete="new-password"
            />
          </FieldWrapper>

          <FieldWrapper label='Passwort bestätigen'>
            <FormInput
              name="confirmPassword"
              control={control}
              type="password"
              placeholder="Passwort wiederholen"
              autoComplete="new-password"
            />
          </FieldWrapper>

          {errors.root?.message && (
            <div className={styles.error} role="alert">
              {errors.root.message}
            </div>
          )}

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Speichern…' : 'Passwort speichern'}
          </Button>

          <div className={styles.footer}>
            <Link to={routes.login}>Zurück zur Anmeldung</Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ResetPasswordPage
